import { useTranslation } from "react-i18next";
import { Grid, Button, Skeleton } from "@mui/material";
import MUIDataTable from "mui-datatables";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { NoInfoComponent } from "../../NoInfoImage/noInfoComponent";
import { useEffect, useState } from "react";

import { getUser, getUserStatus } from "../../../../pages/userSlice";
import { useSelector } from "react-redux";
import { CODES } from "../../../../consts/codes";
import { avisosFields } from "../../../../consts/avisosFields";

import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import CustomTablePagination from "../../../DataTable/CustomTablePagination";
import { GetDynamicReportConf } from "../../../../services/user/infonavit/adminDynamicReport";
import {
  GetEmployeesInfo,
  GetEmployeesReport,
} from "../../../../services/user/infonavit/homeRequests";
import { formatMxCurrency } from "../../../../utils/functions";

export const HomeTable = ({ nrpList }) => {
  const getMuiTheme = () =>
    createTheme({
      components: {
        MuiPaper: {
          styleOverrides: {
            root: {
              boxShadow: "none",
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            footer: {
              border: "none",
            },
          },
        },
        MUIDataTableHeadCell: {
          styleOverrides: {
            contentWrapper: {
              justifyContent: "center",
            },
            toolButton: {
              // * Secreto para que los headers del datatable queden centrados con el contenido
              margin: "0",
              padding: "0",
            },
          },
        },
        MUIDataTableBodyCell: {
          styleOverrides: {
            root: {
              textAlign: "center",
            },
          },
        },
      },
    });

  const muiCache = createCache({
    key: "mui",
    prepend: true,
  });

  const userInfo = useSelector(getUser);
  const userStatus = useSelector(getUserStatus);

  const [data, setData] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const { t } = useTranslation();

  const [columns, setColumns] = useState([]);

  const options = {
    print: "false",
    filter: false,
    download: false,
    search: false,
    selectableRows: "none",
    responsive: "standard",
    elevation: 0,
    viewColumns: false,
    rowsPerPageOptions: [10, 15, 20],
    textLabels: {
      body: {
        noMatch: isLoadingData ? (
          <Skeleton />
        ) : (
          <NoInfoComponent type="dynamic" text="" />
        ),
        toolTip: "Sort",

        columnHeaderTooltip: (column) =>
          t("App.buttonLabels.order", { label: column.label }),
      },
      pagination: {
        next: t("App.userDetail.next"),
        previous: t("App.userDetail.previous"),
        rowsPerPage: t("App.userDetail.resultsPerPage"),
        displayRows: t("App.userDetail.of"),
      },
    },
  };

  /**
   * Carga los NRPs y empleados
   */
  useEffect(() => {
    if (userStatus !== "succeeded") return;

    if (columns.length < 1) fetchConfiguration();
    if (nrpList.length > 0) fetchEmployeesAvisos();
  }, [userStatus, nrpList]);

  /**
   * Función que consulta y settea las columnas de la tabla
   */
  const fetchConfiguration = async () => {
    try {
      const mainUser = userInfo.mainUser;

      setIsLoadingData(true);

      const {
        status,
        data: { responseStatus, response },
      } = await GetDynamicReportConf(mainUser);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        let rpFields = avisosFields;
        const originalReportFields = response?.reportFields ?? [];

        rpFields =
          originalReportFields.length < 1
            ? rpFields
            : rpFields.filter((field) =>
                originalReportFields.includes(field.name)
              );

        rpFields = rpFields.map((field, index) => {
          return {
            ...field,
            options: {
              filter: false,
              sort: true,
              searchable: true,
            },
          };
        });

        setColumns(rpFields);
      } else {
        setColumns([]);
      }

      setIsLoadingData(false);
    } catch (error) {
      console.log("====================================");
      console.log(error);
      setColumns([]);
      console.log("====================================");
    }
  };

  /**
   * Consulta los empleados (avisos) descargados de Infonavit
   */
  const fetchEmployeesAvisos = async () => {
    try {
      setIsLoadingData(true);

      const payload = {
        nrps: nrpList,
        mainUser: userInfo.mainUser,
      };

      const {
        status,
        data: { responseStatus, response },
      } = await GetEmployeesInfo(payload);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        setData(
          response.map((i) => {
            i.facDescuento = formatMxCurrency(i?.facDescuento ?? "0");
            i.monDescuento = formatMxCurrency(i?.monDescuento ?? "0");

            return i;
          })
        );
      } else {
        setData([]);
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    } finally {
      setIsLoadingData(false);
    }
  };

  /**
   * Descarga del reporte de empleados
   */
  const handleDownloadReport = async () => {
    try {
      const payload = {
        nrps: nrpList,
        mainUser: userInfo.mainUser,
      };

      const {
        status,
        data: { responseStatus, response },
      } = await GetEmployeesReport(payload);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        if (response?.fileContent) {
          const a = document.createElement("a");
          a.href = "data:image/png;base64," + response?.fileContent;
          a.download = response?.fileName;
          a.click();
        }
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  return (
    <Grid container gap="2.25rem">
      {/* Table header */}
      <Grid item xs={12}>
        <Grid container gap="1.5rem">
          <Grid
            item
            xs={12}
            lg="auto"
            textAlign={{ xs: "center", sm: "inherit" }}
          >
            <label className="display-large__primary-one">
              {t("App.infonavitDashboard.client.table.transmissionsStatus")}
            </label>
          </Grid>

          <Grid item xs={12} lg>
            <Grid
              container
              gap="3rem"
              justifyContent={{ xs: "space-between", lg: "flex-end" }}
              alignItems={"center"}
            >
              <Grid
                item
                xs="12"
                sm
                lg="auto"
                className="subheading__text-black"
                textAlign={{ xs: "center", sm: "left" }}
              >
                {t("App.infonavitDashboard.client.table.paragraph")}
              </Grid>

              <Grid
                item
                xs="12"
                sm="auto"
                lg="auto"
                textAlign={{ xs: "center", sm: "inherit" }}
              >
                <Button
                  onClick={handleDownloadReport}
                  className="button-transmission"
                  startIcon={<KeyboardArrowRightIcon />}
                >
                  {t("App.infonavitDashboard.client.table.buttonTransmission")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Table */}
      <Grid item xs={12}>
        <CacheProvider value={muiCache}>
          <ThemeProvider theme={getMuiTheme()}>
            <MUIDataTable
              className="body-dataTable"
              components={{
                TableToolbar(props) {
                  return null;
                },
                TableFooter: (props) => (
                  <CustomTablePagination paginationProps={props} />
                ),
              }}
              data={data}
              columns={columns}
              options={options}
            />
          </ThemeProvider>
        </CacheProvider>
      </Grid>
    </Grid>
  );
};
